import React, { forwardRef, memo, useRef, useState } from "react"
import { Box, Flex, FlexProps, Text, Button } from "@chakra-ui/react"
import DatePicker from "react-datepicker"

import { FormInput, Props as FormInputProps } from "./FormInput"
import { useMedia } from "@app/hooks/useMedia"

type Props = Omit<FormInputProps, "value" | "onChange"> & {
  containerProps?: FlexProps
  name: string
  onChange: (value: Date | string) => void
  value: string | Date
  dateFormat: string
  minDate?: Date
  maxDate?: Date
}

export const DatePickerStyles = {
  ".react-datepicker-wrapper": { w: "full" },
  ".react-datepicker-popper": { zIndex: 5 },
  ".react-datepicker": {
    bg: "white",
    border: "1px solid primary.greyDisabled",
    borderRadius: "14px",
    boxShadow: "none",
    fontFamily: "inherit",
    paddingBottom: "50px",
  },
  ".react-datepicker__triangle": { d: "none" },
  ".react-datepicker__header": { borderBottom: "none", bg: "none" },
  ".react-datepicker__header *": { color: "brand.darkBlue" },
  ".react-datepicker__header select option": { color: "black" },
  ".react-datepicker__month-select:focus, .react-datepicker__year-select:focus": { outline: "none" },
  ".react-datepicker .react-datepicker__navigation-icon::before": {
    transform: "borderColor 0.2s ease",
    borderColor: "brand.blue",
  },
  ".react-datepicker .react-datepicker__navigation-icon:hover::before": { borderColor: "brand.blue" },
  ".react-datepicker__navigation": { top: "10px" },
  ".react-datepicker__day--keyboard-selected, .react-datepicker__month-text--keyboard-selected, .react-datepicker__quarter-text--keyboard-selected, .react-datepicker__year-text--keyboard-selecte":
    { bg: "transparent", color: "inherit" },
  ".react-datepicker__day--outside-month": { color: "brand.offBlue" },
  ".react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name": { transition: "all 0.2s ease" },
  ".react-datepicker__day--today, .react-datepicker__month-text--today, .react-datepicker__quarter-text--today, .react-datepicker__year-text--today":
    { fontWeight: 900 },
  ".react-datepicker__day:hover, .react-datepicker__month-text:hover, .react-datepicker__quarter-text:hover, .react-datepicker__year-text:hover":
    { bg: "brand.offBlue" },
  ".react-datepicker__day--disabled:hover, .react-datepicker__day--outside-month:hover, .react-datepicker__day--outside-month.react-datepicker__day--selected":
    { color: "white" },
  ".react-datepicker__day--selected, .react-datepicker__day--selected:hover": { bg: "brand.offBlue" },
  ".react-datepicker__aria-live": { display: "none" },
}

export const DateInput: React.FC<Props> = memo(
  ({ containerProps, description = false, name, onChange, value, dateFormat = "dd/MM/yyyy", minDate, maxDate, ...props }): JSX.Element => {
    const input = useRef<React.Ref<HTMLInputElement>>(null)
    const [showDatePicker, setShowDatePicker] = useState(true)
    const { isTouch } = useMedia()

    const formatDateForInput = (date?: Date | string) => {
      if (!date || typeof date === "string") return ""
      const year = date.getFullYear()
      const month = ("0" + (date.getMonth() + 1)).slice(-2)
      const day = ("0" + date.getDate()).slice(-2)
      return `${year}-${month}-${day}`
    }

    const Input = forwardRef(({ onFocus, value }, ref) => (
      <FormInput
        {...props}
        containerProps={{ pt: 0, w: "full" }}
        inputMode="none"
        isReadOnly
        name={name}
        onChange={() => null}
        onFocus={onFocus}
        reference={ref}
        value={value}
      />
    ))

    const handleInputClick = () => {
      setShowDatePicker(true)
    }

    return (
      <Box {...containerProps}>
        <Box
          onClick={isTouch ? () => input?.current?.showPicker() : () => null}
          alignItems="center"
          pos="relative"
          w="100%"
          sx={DatePickerStyles}
        >
          {isTouch ? (
            <FormInput
              name={name}
              type="date"
              reference={input}
              value={formatDateForInput(value)}
              onChange={e => onChange(new Date(e?.target?.value))}
              min={minDate ? formatDateForInput(minDate) : undefined}
              max={maxDate ? formatDateForInput(maxDate) : undefined}
              {...props}
            />
          ) : showDatePicker ? (
            <Flex direction="column" alignItems="center">
              <DatePicker
                ref={input}
                calendarStartDay={1}
                customInput={<Input />}
                dateFormat={dateFormat}
                inline
                onChange={onChange}
                selected={value}
                minDate={minDate}
                maxDate={maxDate}
              >
                <Flex
                  justifyContent="space-between"
                  className="picker-additional-buttons"
                  width="100%"
                  position="absolute"
                  bottom="2.5"
                  px="2.5"
                  pt="1.25"
                  borderTop="1px solid"
                  borderColor="primary.greyDisabled"
                  color="brand.blue"
                  fontSize="sm"
                >
                  <Button onClick={() => onChange("")} p={2} title="reset date" aria-label="reset date">
                    Reset
                  </Button>

                  <Button onClick={() => setShowDatePicker(false)} p={2} title="close datepicker" aria-label="close datepicker">
                    Done
                  </Button>
                </Flex>
              </DatePicker>
            </Flex>
          ) : (
            <FormInput
              name={name}
              type="text"
              onClick={handleInputClick}
              value={typeof value === "string" ? value : value?.toLocaleDateString()}
              onChange={e => onChange(new Date(e.target.value))}
              {...props}
            />
          )}
        </Box>
        {description && (
          <Text mb={-0.75} mt={2} size="xs" textAlign="center">
            {description}
          </Text>
        )}
      </Box>
    )
  }
)
